import { useId, useState } from "react";
import { kindsText } from "../inputCards/typeWords";
import { useTranslation } from "react-i18next";
import type { WorkflowContent } from "../../../api/content/authoring/processContentDecoders";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { KEY_NAME_PATTERN } from "../../processAuthoring/steps/stepDefinitions";
import type { WorkflowDraftAction } from "../model/workflowDraft";
import { workflowParts, type PortCatalogue } from "../model/workflowPorts";
import EmptyState from "../../../ui/feedback/EmptyState";

/**
 * The Workflow's exports (AU-6): named parts other Workflows can bind when they use this
 * one as a sub-workflow. This is how Workflows are linked.
 */
export default function ExportsPanel({ draft, ports, dispatch }: { draft: WorkflowContent; ports: PortCatalogue; dispatch: (action: WorkflowDraftAction) => void }) {
  const { t } = useTranslation();
  const headingId = useId();
  const parts = workflowParts(draft, ports);
  const [name, setName] = useState("");
  const [part, setPart] = useState("");
  const chosen = parts.find((candidate) => `${candidate.from.from_step}.${candidate.from.part}` === part);
  const nameProblem = name !== "" && (!KEY_NAME_PATTERN.test(name) || name in draft.exports);

  return (
    <section className="app-card space-y-2 p-3" aria-labelledby={headingId}>
      <h3 id={headingId} className="app-title text-sm font-semibold">{t("workflows.edit.exports")}</h3>
      {Object.keys(draft.exports).length === 0 ? <EmptyState size="small">{t("workflows.edit.noExports")}</EmptyState> : null}
      <ul className="space-y-1 text-xs">
        {Object.entries(draft.exports).map(([exported, from]) => (
          <li key={exported} className="flex items-center gap-2">
            <span className="font-mono font-semibold">{exported}</span>
            <span className="app-text-muted min-w-0 flex-1 truncate font-mono">← {from.from_step}.{from.part}</span>
            <AppButton variant="ghost" size="xs" aria-label={t("workflows.edit.removeExportNamed", { name: exported })} onClick={() => { dispatch({ type: "removeExport", name: exported }); }}>
              {t("workflows.edit.remove")}
            </AppButton>
          </li>
        ))}
      </ul>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (chosen !== undefined && name !== "" && !nameProblem) {
            dispatch({ type: "setExport", name, from: chosen.from });
            setName("");
            setPart("");
          }
        }}
      >
        <input className={`${FORM_COMPACT_CONTROL_CLASS} w-36 font-mono`} aria-label={t("workflows.edit.exportName")} aria-invalid={nameProblem} placeholder={t("workflows.edit.exportName")} value={name} onChange={(event) => { setName(event.target.value); }} />
        <select
          className={`${FORM_SELECT_CLASS} w-auto text-xs`}
          aria-label={t("workflows.edit.exportPart")}
          value={part}
          onChange={(event) => {
            setPart(event.target.value);
            const picked = parts.find((candidate) => `${candidate.from.from_step}.${candidate.from.part}` === event.target.value);
            if (name === "" && picked !== undefined) {
              setName(picked.from.part);
            }
          }}
        >
          <option value="">{t("workflows.edit.exportPart")}</option>
          {parts.map((candidate) => (
            <option key={`${candidate.from.from_step}.${candidate.from.part}`} value={`${candidate.from.from_step}.${candidate.from.part}`}>
              {candidate.from.from_step}.{candidate.from.part} · {kindsText(candidate.kinds, t)}
            </option>
          ))}
        </select>
        <AppButton type="submit" variant="secondary" size="xs" disabled={chosen === undefined || name === "" || nameProblem}>{t("workflows.edit.addExport")}</AppButton>
      </form>
      {nameProblem ? <p className="app-error-text text-xs">{t("workflows.edit.exportNameProblem")}</p> : null}
    </section>
  );
}
